import { useSyncOuEnLigne as useSync } from '../context/SyncContext';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  ArrowLeft,
  Banknote,
  Building2,
  ChevronRight,
  Copy,
  Download,
  Eye,
  FileText,
  Gavel,
  History,
  LayoutList,
  MailWarning,
  Paperclip,
  Pencil,
  Plus,
  Rows3,
  Search,
  Send,
  Share2,
  StickyNote,
  Trash2,
} from 'lucide-react';
import { clientsAPI, invoicesAPI } from '../lib/api';
import { publicOrigin } from '../lib/desktop';
import { fetchAllPages } from '../lib/fetchAllPages';
import { downloadBlob } from '../lib/download';
import { messageErreur } from '../lib/erreurs';
import { libelle, referenceLisible } from '../lib/libelles';
import { Badge } from '../components/ui/badge';
import { Button } from '../components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '../components/ui/dialog';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '../components/ui/select';
import { SearchableSelect } from '../components/ui/searchable-select';
import { Textarea } from '../components/ui/textarea';
import {
  Tabs,
  TabsContent,
  TabsList,
  TabsTrigger,
} from '../components/ui/tabs';
import { toast } from '../components/ui/sonner';
import { EncaisserClient } from '../components/finance/NouvelleOperation';
import BusinessActionContract from '../components/business/BusinessActionContract';
import {
  invoiceBlockers,
  invoiceContract,
} from '../lib/businessContracts';
import {
  cn,
  formatCompactCurrency,
  formatCurrency,
  formatDate,
} from '../lib/utils';
import {
  arrondiDevise,
  formatPrixUnitaire,
  francisaTexte,
  totauxFacture,
} from '../lib/formats';
import { estFactureOuverte, montantFacture, resteFacture, sommeMontants } from '../lib/montantsFacture';
import { PageHeader } from '../components/ui/page-header';
import { ColonneFixe, SplitFixe } from '../components/ui/page-layouts';
import { Aide } from '../components/ui/aide';
import { useRafraichirSur } from '../hooks/useRafraichirSur';
import { usePagination } from '../hooks/usePagination';
import { BarrePagination } from '../components/ui/pagination-bar';
import { LEXIQUE } from '../lib/lexique';
import { LoadingState } from '../components/ui/loading-state';
import { EmptyState } from '../components/ui/empty-state';
import { ChampsAvances } from '../components/ui/champs-avances';

const STATUTS_FACTURE = ['DRAFT', 'PENDING', 'SENT', 'PARTIAL', 'PAID', 'DISPUTED', 'CANCELLED'];

/** Statut d'une facture en français; une facture sans statut est un brouillon. */
const statutFacture = (status) => libelle('invoiceStatus', status, 'Brouillon');

/** Numéro de facture à citer; « DRAFT » est le numéro provisoire posé avant l'attribution du vrai. */
const refFacture = (invoice, repli = 'Sans numéro') => {
  const numero = invoice?.invoice_number;
  if (!numero || String(numero).toUpperCase() === 'DRAFT') return repli;
  return referenceLisible(numero, repli);
};

// Chaque bandeau porte du texte: il lui faut donc la couleur d'encre prévue
// par le jeton de son fond. Un blanc en dur passait sous silence le fait que
// le bandeau Proforma et le bandeau Premium n'ont pas le même contraste.
const templateOptions = {
  Standard: {
    label: 'Standard',
    description: 'Administratif, clair, très lisible.',
    accent: 'border-border bg-muted/50',
    band: 'bg-readout',
    bandText: 'text-readout-foreground',
  },
  Proforma: {
    label: 'Proforma',
    description: 'Document non définitif avant validation.',
    accent: 'border-info/40 bg-info/10',
    band: 'bg-info',
    bandText: 'text-info-foreground',
  },
  Premium: {
    label: 'Premium',
    description: 'Présentation commerciale plus haut de gamme.',
    accent: 'border-flame/40 bg-flame/10',
    band: 'bg-flame',
    bandText: 'text-flame-foreground',
  },
};

/**
 * La durée de vie du lien client, en durées nommées.
 *
 * Le champ demandait « 604800 » à un exploitant. Le serveur borne entre une
 * heure et trente jours: les trois durées tiennent dans ces bornes, et la
 * semaine reste le défaut.
 */
const VALIDITES_DE_LIEN = [
  { valeur: String(24 * 3600), libelle: '24 heures' },
  { valeur: String(7 * 24 * 3600), libelle: '7 jours' },
  { valeur: String(30 * 24 * 3600), libelle: '30 jours' },
];

const VALIDITE_PAR_DEFAUT = String(7 * 24 * 3600);


/** « 1 facture » / « 4 factures »: un pluriel faux décrédibilise un compteur. */
const libelleFactures = (nombre) => `${nombre} facture${nombre > 1 ? 's' : ''}`;

const horodatage = (value) => (value ? new Date(value).getTime() || 0 : 0);

/**
 * Tri local de la file.
 *
 * La liste est déjà chargée en mémoire (200 pièces au plus): trier côté client
 * évite un aller-retour réseau pour ce qui n'est qu'une préférence d'affichage.
 */
const comparateursTri = {
  RECENT: (a, b) => horodatage(b.invoice_date) - horodatage(a.invoice_date),
  ANCIENNE: (a, b) => horodatage(a.invoice_date) - horodatage(b.invoice_date),
  MONTANT: (a, b) => Number(b.total_amount || 0) - Number(a.total_amount || 0),
};

/** Le total d'une ligne peut venir du serveur; sinon on le recalcule. */
const totalLigne = (line) =>
  line.total || Number(line.quantity || 0) * Number(line.unit_price || 0);

/** Les lignes manquantes (import ancien, facture d'appoint) sont reconstruites
 *  à partir du montant, sinon le tableau du détail s'affiche vide. */
const lignesDeFacture = (invoice) =>
  invoice.line_items?.length
    ? invoice.line_items
    : [
        {
          description: invoice.notes || 'Prestation carburant',
          quantity: 1,
          unit_price: invoice.subtotal || invoice.total_amount || 0,
          total: invoice.subtotal || invoice.total_amount || 0,
        },
      ];

const defaultLine = () => ({
  id: crypto.randomUUID(),
  description: 'Livraison carburant',
  quantity: '1',
  unit_price: '',
});

const CONDITIONS_PAR_DEFAUT = 'Paiement selon les conditions convenues.';

const defaultForm = () => {
  const emission = new Date().toISOString().slice(0, 10);
  return {
    client_id: '',
    invoice_date: emission,
    // Vide et non « 0 »: la TVA n'est pas une donnée de la facture tant que
    // personne ne l'a posée, et un zéro écrit d'avance ouvrirait le dépliant
    // des détails à chaque création en se faisant passer pour une saisie.
    // Le total ne change pas — `computeTotals` lit `Number(taxRate || 0)`.
    tax_rate: '',
    template_name: 'Standard',
    notes: '',
    terms_and_conditions: CONDITIONS_PAR_DEFAUT,
    line_items: [defaultLine()],
    subtotal: 0,
    tax_amount: 0,
    total_amount: 0,
  };
};

const toDateInput = (value) => (value ? String(value).slice(0, 10) : '');

const normalizeTemplate = (value) => (
  ['Standard', 'Proforma', 'Premium'].includes(value) ? value : 'Standard'
);

// Mêmes arrondis que le serveur (ligne, somme, TVA, total): l'aperçu annonce
// le net à payer que la facture enregistrée portera, au franc près.
const computeTotals = (lineItems, taxRate) => {
  const { subtotal, tax_amount, total_amount } = totauxFacture(lineItems, taxRate);
  return { subtotal, tax_amount, total_amount };
};

const invoiceToForm = (invoice) => {
  const lines = (invoice?.line_items || []).length
    ? invoice.line_items.map((item) => ({
        id: item.id || crypto.randomUUID(),
        description: item.description || '',
        quantity: String(item.quantity ?? ''),
        unit_price: String(item.unit_price ?? ''),
        order_id: item.order_id,
        trip_id: item.trip_id,
      }))
    : [{
        description: invoice?.notes || 'Prestation carburant',
        quantity: '1',
        unit_price: String(invoice?.subtotal || invoice?.total_amount || ''),
      }];
  const form = {
    client_id: invoice?.client_id || '',
    invoice_date: toDateInput(invoice?.invoice_date) || new Date().toISOString().slice(0, 10),
    tax_rate: String(invoice?.tax_rate ?? 0),
    template_name: normalizeTemplate(invoice?.template_name),
    notes: invoice?.notes || '',
    terms_and_conditions: invoice?.terms_and_conditions || CONDITIONS_PAR_DEFAUT,
    line_items: lines,
  };
  return { ...form, ...computeTotals(lines, form.tax_rate) };
};

export default function InvoicesPage() {
  const { isOnline } = useSync();
  const [searchParams] = useSearchParams();
  const handledInvoiceActionRef = useRef('');
  const [invoices, setInvoices] = useState([]);
  const [clients, setClients] = useState([]);
  const [selectedInvoiceId, setSelectedInvoiceId] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [statusFilter, setStatusFilter] = useState('ALL');
  // Deux natures de pièce dans la même liste: une facture de commande et une
  // facture de location pèsent sur le même encours client, mais elles ne se
  // relancent ni ne se justifient de la même façon.
  const [kindFilter, setKindFilter] = useState(
    (searchParams.get('kind') || 'ALL').toUpperCase(),
  );
  const [sortOrder, setSortOrder] = useState('RECENT');
  // Deux façons de lire la même file: le relanceur balaie quarante numéros,
  // le comptable pointe client et date ligne à ligne.
  const [compactList, setCompactList] = useState(false);
  // Maître-détail: sur téléphone les deux panneaux ne tiennent pas côte à
  // côte, la sélection bascule donc en plein écran avec un retour explicite.
  const [mobileDetailOpen, setMobileDetailOpen] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [showEditor, setShowEditor] = useState(false);
  const [editorMode, setEditorMode] = useState('create');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  // « Encaisser »: la même fenêtre que l'écran Argent (compte de caisse ou de
  // banque, facture réglée choisie d'avance, crédit au client au-delà du reste).
  const [encaissement, setEncaissement] = useState(null);
  const [showReminderModal, setShowReminderModal] = useState(false);
  const [showDisputeModal, setShowDisputeModal] = useState(false);
  const [showDispatchModal, setShowDispatchModal] = useState(false);
  const [selectedInvoice, setSelectedInvoice] = useState(null);
  const [reminderData, setReminderData] = useState({
    message: '',
    due_date: '',
  });
  const [disputeReason, setDisputeReason] = useState('');
  const [dispatchData, setDispatchData] = useState({
    channel: 'EMAIL',
    recipient: '',
    message: '',
    expires_in_seconds: VALIDITE_PAR_DEFAUT,
  });
  const [dispatchResult, setDispatchResult] = useState(null);
  const [formData, setFormData] = useState(defaultForm);

  const loadInvoices = useCallback(async ({ silencieux = false } = {}) => {
    if (!silencieux) setIsLoading(true);
    try {
      const [invoicesResult, clientsResult] = await Promise.all([
        fetchAllPages((page) => invoicesAPI.getAllPaged(page)),
        fetchAllPages((page) => clientsAPI.getAllPaged(page)),
      ]);
      const items = invoicesResult.items || [];
      const requestedInvoiceId = searchParams.get('invoice_id');
      setInvoices(items);
      setClients(clientsResult.items || []);
      setSelectedInvoiceId((prev) => {
        if (requestedInvoiceId && items.some((invoice) => invoice.id === requestedInvoiceId)) {
          return requestedInvoiceId;
        }
        return prev || items[0]?.id || null;
      });
    } catch (err) {
      console.error('Failed to load invoices:', err);
      toast.error(messageErreur(err, 'Impossible de charger les factures. Réessayez.'));
    } finally {
      setIsLoading(false);
    }
  }, [searchParams]);

  useEffect(() => {
    loadInvoices();
  }, [loadInvoices]);

  // Un paiement, un avoir ou une facture créés ailleurs (Finances, Clients,
  // Commandes, un autre onglet) changent la liste et les compteurs d'en-tête.
  useRafraichirSur(['factures', 'paiements', 'clients'], loadInvoices);


  const STORAGE_PREFIX = 'app_v1_';

  useEffect(() => {
    let saved = {};
    try {
      saved = JSON.parse(
        localStorage.getItem(STORAGE_PREFIX + 'invoicesFilters') ||
          localStorage.getItem('invoicesFilters') ||
          '{}',
      );
    } catch (error) {
      // A corrupted browser preference must not prevent the invoices page from
      // rendering during a demo.
      console.warn('Invalid invoices filters in local storage; using defaults.');
    }
    if (saved.searchQuery) setSearchQuery(saved.searchQuery);
    // « En retard » n'existe plus comme statut: un filtre mémorisé dessus
    // viderait la liste sans qu'on puisse le défaire.
    if (saved.statusFilter && saved.statusFilter !== 'OVERDUE') setStatusFilter(saved.statusFilter);
  }, []);

  useEffect(() => {
    localStorage.setItem(STORAGE_PREFIX + 'invoicesFilters', JSON.stringify({ searchQuery, statusFilter }));
  }, [searchQuery, statusFilter]);

  const clientsById = useMemo(
    () => new Map(clients.map((client) => [client.id, client])),
    [clients],
  );

  const enrichedInvoices = useMemo(() => invoices.map((invoice) => ({
    ...invoice,
    // Les factures n'ont pas d'échéance: d'anciennes factures « en retard »
    // sont simplement à encaisser.
    status: invoice.status === 'OVERDUE' ? 'PENDING' : invoice.status,
    client: clientsById.get(invoice.client_id),
    amount_remaining: invoice.amount_remaining ?? invoice.balance_due ?? invoice.total_amount ?? 0,
    amount_paid: invoice.amount_paid ?? invoice.paid_amount ?? 0,
    template_name: normalizeTemplate(invoice.template_name),
  })), [invoices, clientsById]);

  // Cet effet lit `enrichedInvoices` dans ses dépendances: placé avant sa
  // définition, le tableau était évalué au rendu et levait une
  // ReferenceError — l'écran Factures ne s'affichait plus du tout.
  useEffect(() => {
    const requestedInvoiceId = searchParams.get('invoice_id');
    const requestedAction = searchParams.get('invoice_action');
    if (!requestedInvoiceId || !requestedAction) {
      handledInvoiceActionRef.current = '';
      return;
    }
    const currentKey = `${requestedInvoiceId}:${requestedAction}`;
    if (handledInvoiceActionRef.current === currentKey) {
      return;
    }
    const targetInvoice = enrichedInvoices.find((invoice) => invoice.id === requestedInvoiceId);
    if (!targetInvoice) {
      return;
    }

    if (requestedAction === 'payment') {
      setSelectedInvoice(targetInvoice);
      setEncaissement(targetInvoice);
    } else if (requestedAction === 'reminder') {
      setSelectedInvoice(targetInvoice);
      setReminderData({ message: '', due_date: '' });
      setShowReminderModal(true);
    } else if (requestedAction === 'dispute') {
      setSelectedInvoice(targetInvoice);
      setDisputeReason('');
      setShowDisputeModal(true);
    } else if (requestedAction === 'dispatch') {
      setSelectedInvoice(targetInvoice);
      setDispatchResult(null);
      setDispatchData({
        channel: 'EMAIL',
        recipient: targetInvoice?.client?.email || '',
        message: '',
        expires_in_seconds: VALIDITE_PAR_DEFAUT,
      });
      setShowDispatchModal(true);
    } else {
      return;
    }

    handledInvoiceActionRef.current = currentKey;
  }, [enrichedInvoices, searchParams]);

  const filtered = enrichedInvoices.filter((invoice) => {
    const clientName = invoice.client?.name || '';
    const statusMatch =
      statusFilter === 'ALL' ||
      invoice.status === statusFilter ||
      (statusFilter === 'PARTIAL' && invoice.status === 'PARTIALLY_PAID');
    // L'historique n'a pas de nature: une facture antérieure au module
    // location est une facture de commande.
    const kindMatch =
      kindFilter === 'ALL' || (invoice.invoice_kind || 'ORDER') === kindFilter;
    const query = searchQuery.toLowerCase();
    const queryMatch =
      String(invoice.invoice_number || '').toLowerCase().includes(query) ||
      clientName.toLowerCase().includes(query);
    return statusMatch && kindMatch && queryMatch;
  });

  // Le tri ne touche pas au filtrage: on copie avant de trier, sinon
  // `enrichedInvoices` serait réordonné en place à chaque rendu.
  const sorted = [...filtered].sort(
    comparateursTri[sortOrder] || comparateursTri.RECENT,
  );
  const pagination = usePagination(sorted, {
    reinitialiser: `${searchQuery}|${statusFilter}|${kindFilter}|${sortOrder}`,
  });

  const currentInvoice = enrichedInvoices.find((invoice) => invoice.id === selectedInvoiceId) || sorted[0] || null;
  const previewInvoice = showEditor
    ? buildPreviewInvoice(formData, clientsById)
    : currentInvoice;

  const stats = useMemo(() => {
    // Mêmes règles que le serveur: un brouillon n'est pas une créance, une
    // facture annulée n'est pas facturée, un avoir réduit le facturé.
    const ouvertes = enrichedInvoices.filter(estFactureOuverte);
    const receivable = sommeMontants(ouvertes.map(resteFacture));
    const encaissees = enrichedInvoices.filter(
      (invoice) => Number(invoice.amount_paid || 0) > 0,
    );
    const paid = sommeMontants(enrichedInvoices.map((invoice) => invoice.amount_paid));
    const facturees = enrichedInvoices.filter((invoice) => String(invoice.status || '').toUpperCase() !== 'CANCELLED');
    const total = sommeMontants(facturees.map(montantFacture));
    // Chaque compteur dit aussi sur combien de pièces il porte: « 12 M FCFA »
    // sur deux factures et sur quarante ne se traitent pas pareil.
    return {
      receivable,
      receivableCount: ouvertes.length,
      paid,
      paidCount: encaissees.length,
      total,
      totalCount: facturees.length,
    };
  }, [enrichedInvoices]);

  // Sélectionner ouvre le détail: sur grand écran il est déjà à droite, sur
  // téléphone il prend la place de la liste.
  const selectInvoice = (invoice) => {
    setSelectedInvoiceId(invoice.id);
    setMobileDetailOpen(true);
  };

  // Sur téléphone la file reste le point de départ — sauf quand rien n'a
  // jamais été facturé: c'est l'écran vide qui porte alors la création, et une
  // liste vide au-dessus n'apprendrait rien.
  const detailPleinEcran = mobileDetailOpen || invoices.length === 0;

  // « Voir l'aperçu » ouvre l'aperçu de la facture dans une fenêtre: il n'est
  // plus posé sous le détail tant que l'utilisateur ne l'a pas demandé.
  const openPreview = () => setShowPreview(true);

  const updateForm = (patch) => {
    setFormData((prev) => {
      const next = { ...prev, ...patch };
      return { ...next, ...computeTotals(next.line_items, next.tax_rate) };
    });
  };

  const openCreate = () => {
    setEditorMode('create');
    setSelectedInvoice(null);
    setFormData(defaultForm());
    setShowEditor(true);
  };

  const openEdit = (invoice) => {
    setEditorMode('edit');
    setSelectedInvoice(invoice);
    setFormData(invoiceToForm(invoice));
    setShowEditor(true);
  };

  const submitEditor = async () => {
    const blockers = invoiceBlockers({ invoice: selectedInvoice, formData, mode: editorMode });
    if (blockers.length) {
      toast.error(blockers[0]);
      return;
    }
    const payload = buildInvoicePayload(formData);
    try {
      if (editorMode === 'edit' && selectedInvoice) {
        const updated = await invoicesAPI.update(selectedInvoice.id, payload);
        setInvoices((prev) => prev.map((invoice) => (invoice.id === updated.id ? updated : invoice)));
        setSelectedInvoiceId(updated.id);
        toast.success('Facture mise à jour');
      } else {
        const created = await invoicesAPI.create({
          ...payload,
          invoice_number: 'DRAFT',
          status: 'PENDING',
          paid_amount: 0,
          balance_due: payload.total_amount,
        });
        setInvoices((prev) => [created, ...prev]);
        setSelectedInvoiceId(created.id);
        toast.success('Facture créée');
      }
      setShowEditor(false);
    } catch (err) {
      console.error('Failed to save invoice:', err);
      toast.error(messageErreur(err, 'Enregistrement de la facture impossible. Réessayez.'));
    }
  };

  const openPayment = (invoice) => {
    setSelectedInvoice(invoice);
    setEncaissement(invoice);
  };

  // L'encaissement passe par le journal (`/finance/operations`): la facture
  // et la liste se relisent sur le serveur.
  const apresEncaissement = () => {
    setEncaissement(null);
    loadInvoices({ silencieux: true });
  };

  const openReminder = (invoice) => {
    setSelectedInvoice(invoice);
    setReminderData({ message: '', due_date: '' });
    setShowReminderModal(true);
  };

  const handleReminder = async () => {
    if (!selectedInvoice || !reminderData.message || !reminderData.due_date) {
      toast.error('Message et date de suivi obligatoires');
      return;
    }
    const reminders = [
      ...(selectedInvoice.reminders || []),
      {
        id: crypto.randomUUID(),
        message: reminderData.message,
        due_date: reminderData.due_date,
        status: 'PENDING',
      },
    ];
    try {
      const updated = await invoicesAPI.update(selectedInvoice.id, { reminders });
      setInvoices((prev) => prev.map((invoice) => (invoice.id === updated.id ? updated : invoice)));
      setShowReminderModal(false);
      toast.success('Relance ajoutée');
    } catch (err) {
      console.error('Failed to add reminder:', err);
      toast.error(messageErreur(err, 'Relance impossible. Réessayez.'));
    }
  };

  const openDispute = (invoice) => {
    setSelectedInvoice(invoice);
    setDisputeReason('');
    setShowDisputeModal(true);
  };

  const openDispatch = (invoice) => {
    setSelectedInvoice(invoice);
    setDispatchResult(null);
    setDispatchData({
      channel: 'EMAIL',
      recipient: invoice?.client?.email || '',
      message: '',
      expires_in_seconds: VALIDITE_PAR_DEFAUT,
    });
    setShowDispatchModal(true);
  };

  const handleDispute = async () => {
    if (!selectedInvoice || !disputeReason) {
      toast.error('Motif de litige obligatoire');
      return;
    }
    try {
      const updated = await invoicesAPI.update(selectedInvoice.id, {
        status: 'DISPUTED',
        dispute_reason: disputeReason,
      });
      setInvoices((prev) => prev.map((invoice) => (invoice.id === updated.id ? updated : invoice)));
      setShowDisputeModal(false);
      toast.success('Litige enregistré');
    } catch (err) {
      console.error('Failed to mark dispute:', err);
      toast.error(messageErreur(err, 'Impossible d’enregistrer le litige. Réessayez.'));
    }
  };

  const copyText = async (text, successMessage = 'Lien copié') => {
    if (!text) return;
    try {
      await navigator.clipboard.writeText(text);
      toast.success(successMessage);
    } catch (err) {
      console.error('Failed to copy text:', err);
      toast.error(messageErreur(err, 'Copie impossible. Copiez le lien à la main.'));
    }
  };

  const handleDispatch = async () => {
    if (!selectedInvoice) return;
    try {
      const payload = {
        ...dispatchData,
        expires_in_seconds: Number(dispatchData.expires_in_seconds || 0),
        recipient: dispatchData.recipient || undefined,
        message: dispatchData.message || undefined,
        app_base_url: publicOrigin(),
      };
      const result = await invoicesAPI.dispatch(selectedInvoice.id, payload);
      if (result?.invoice?.id) {
        setInvoices((prev) => prev.map((invoice) => (invoice.id === result.invoice.id ? result.invoice : invoice)));
        setSelectedInvoiceId(result.invoice.id);
        setSelectedInvoice(result.invoice);
      }
      setDispatchResult(result);
      if (dispatchData.channel === 'EMAIL' && result?.mailto_url) {
        window.open(result.mailto_url, '_blank');
      } else if (dispatchData.channel === 'WHATSAPP' && result?.whatsapp_url) {
        window.open(result.whatsapp_url, '_blank');
      }
      toast.success('Lien de partage prêt');
    } catch (err) {
      console.error('Failed to dispatch invoice:', err);
      toast.error(messageErreur(err, 'Partage de la facture impossible. Réessayez.'));
    }
  };

  const confirmDelete = (invoice) => {
    setSelectedInvoice(invoice);
    setShowDeleteModal(true);
  };

  const handleDelete = async () => {
    if (!selectedInvoice) return;
    try {
      await invoicesAPI.delete(selectedInvoice.id);
      setInvoices((prev) => prev.filter((invoice) => invoice.id !== selectedInvoice.id));
      setSelectedInvoiceId((prev) => (prev === selectedInvoice.id ? null : prev));
      // Sur téléphone, rester sur le détail d'une facture qui n'existe plus
      // n'a pas de sens: on retourne à la file.
      setMobileDetailOpen(false);
      toast.success('Facture supprimée');
    } catch (err) {
      console.error('Failed to delete invoice:', err);
      toast.error(messageErreur(err, 'Suppression impossible. Réessayez.'));
    } finally {
      setShowDeleteModal(false);
      setSelectedInvoice(null);
    }
  };

  const downloadPdf = async (invoice) => {
    if (!invoice?.id) return;
    try {
      const response = await invoicesAPI.downloadPdf(invoice.id);
      downloadBlob(
        response,
        invoice.invoice_number ? `facture-${refFacture(invoice, 'sans-numero')}.pdf` : 'facture.pdf',
        'application/pdf',
      );
      toast.success('PDF généré');
    } catch (err) {
      console.error('Failed to download invoice PDF:', err);
      toast.error(messageErreur(err, 'PDF indisponible pour le moment. Réessayez.'));
    }
  };

  const statsFactures = [
    {
      label: 'À encaisser',
      valeur: formatCompactCurrency(stats.receivable),
      ton: stats.receivable > 0 ? 'attention' : 'neutre',
      aide: `À encaisser sur ${libelleFactures(stats.receivableCount)}`,
    },
    {
      label: 'Encaissés',
      valeur: formatCompactCurrency(stats.paid),
      ton: 'positif',
      aide: `Encaissés sur ${libelleFactures(stats.paidCount)}`,
    },
  ];
  return (
    <div className="mx-auto w-full max-w-[1800px] space-y-5 px-4 pb-5 pt-2 md:px-6 md:pb-6 md:pt-2 lg:px-8">
      <PageHeader
        title="Factures"
        stats={statsFactures}
        statsDisposition="ligne"
        actions={
          <Button onClick={openCreate} disabled={!isOnline}>
            <Plus className="h-4 w-4" aria-hidden="true" />
            Nouvelle facture
          </Button>
        }
      />

      {/* Maître-détail: la file à gauche (~40 %), la facture ouverte à droite
          (~60 %). Sur téléphone un seul panneau est monté à la fois — deux
          colonnes de 180 px ne se lisent ni l'une ni l'autre. */}
      <SplitFixe colonnes="lg:grid-cols-[minmax(0,2fr)_minmax(0,3fr)]" data-testid="factures-colonnes">
        {/* Dès lg, chaque colonne se colle sous la barre du haut puis défile
            dans son propre cadre (voir `ColonneFixe`): la file ne rogne plus
            le détail, et le détail ne pousse plus la file hors de l'écran. */}
        <ColonneFixe className={cn(detailPleinEcran && 'hidden lg:block')} data-testid="factures-colonne-liste">
          <section className="rounded-2xl border border-border/70 bg-card shadow-sm">
            {/* En-tête de la liste: titre, recherche et filtres restent visibles
                tant que la file défile dans sa colonne (dès lg). */}
            <div className="space-y-3 rounded-t-2xl border-b border-border/60 bg-card px-4 pb-3 pt-4 md:px-5 md:pt-5 lg:sticky lg:top-0 lg:z-10">
              <div className="flex min-w-0 items-center gap-3">
                <span className="flex h-9 w-9 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                  <FileText className="h-[18px] w-[18px]" />
                </span>
                <div className="min-w-0">
                  <h2 className="truncate text-base font-bold leading-tight text-foreground">Liste des factures</h2>
                  <p className="truncate text-xs text-muted-foreground">{libelleFactures(sorted.length)}</p>
                </div>
              </div>
              {/* Recherche et bouton d'affichage sur la même ligne. */}
              <div className="flex items-center gap-2">
                <div className="relative min-w-0 flex-1">
                  <Search
                    className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
                    aria-hidden="true"
                  />
                  <Input
                    className="pl-9"
                    placeholder="Rechercher une facture, un client…"
                    value={searchQuery}
                    onChange={(event) => setSearchQuery(event.target.value)}
                  />
                </div>
                {/* Bouton d'affichage: il ne filtre rien, il change la densité de
                    la file. Le libellé est porté par `title`/`aria-label` parce
                    qu'une icône seule ne dit pas ce qu'elle fera. */}
                <Button
                  variant="outline"
                  size="icon"
                  className="shrink-0"
                  aria-pressed={compactList}
                  aria-label={compactList ? 'Afficher la liste détaillée' : 'Afficher la liste compacte'}
                  title={compactList ? 'Affichage détaillé' : 'Affichage compact'}
                  onClick={() => setCompactList((prev) => !prev)}
                >
                  {compactList ? (
                    <LayoutList className="h-4 w-4" aria-hidden="true" />
                  ) : (
                    <Rows3 className="h-4 w-4" aria-hidden="true" />
                  )}
                </Button>
              </div>
              {/* Les trois filtres (statut, nature, tri) sur une seule ligne. */}
              <div className="grid grid-cols-3 gap-2">
                <Select value={statusFilter} onValueChange={setStatusFilter}>
                  <SelectTrigger className="w-full min-w-0 px-2.5">
                    <SelectValue placeholder="Tous les statuts" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Tous les statuts</SelectItem>
                    {STATUTS_FACTURE.map((value) => (
                      <SelectItem key={value} value={value}>{statutFacture(value)}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                <Select value={kindFilter} onValueChange={setKindFilter}>
                  <SelectTrigger className="w-full min-w-0 px-2.5">
                    <SelectValue placeholder="Toutes les natures" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="ALL">Toutes les natures</SelectItem>
                    <SelectItem value="ORDER">Commandes</SelectItem>
                    <SelectItem value="RENTAL">Locations</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={sortOrder} onValueChange={setSortOrder}>
                  <SelectTrigger className="w-full min-w-0 px-2.5" aria-label="Trier les factures">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="RECENT">Plus récente</SelectItem>
                    <SelectItem value="ANCIENNE">Plus ancienne</SelectItem>
                    <SelectItem value="MONTANT">Montant décroissant</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>
            <div className="space-y-3 px-4 pb-4 pt-4 md:px-5 md:pb-5">
              <InvoiceQueue
                invoices={pagination.lignes}
                selectedId={currentInvoice?.id}
                loading={isLoading}
                compact={compactList}
                onSelect={selectInvoice}
              />
              {!isLoading && sorted.length > 0 ? <BarrePagination pagination={pagination} nom="factures" /> : null}
            </div>
          </section>
        </ColonneFixe>

        <ColonneFixe
          className={cn('space-y-4', !detailPleinEcran && 'hidden lg:block')}
          data-testid="factures-colonne-detail"
        >
          {/* Sans ce retour, le détail plein écran est un cul-de-sac sur
              téléphone: la file n'est plus montée. */}
          <Button
            variant="ghost"
            className="lg:hidden"
            onClick={() => setMobileDetailOpen(false)}
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Retour à la liste
          </Button>

          {currentInvoice ? (
            <>
              <InvoiceDetail
                invoice={currentInvoice}
                onEdit={openEdit}
                onPayment={openPayment}
                onReminder={openReminder}
                onDispute={openDispute}
                onDispatch={openDispatch}
                onDelete={confirmDelete}
                onDownload={downloadPdf}
                onPreview={openPreview}
              />
              <InvoiceBusinessReadiness invoice={currentInvoice} />
            </>
          ) : (
            /* Deux situations très différentes derrière le même écran vide:
               rien n'a encore été facturé, ou rien n'est sélectionné dans une
               liste qui, elle, contient quelque chose. */
            <EmptyState
              variant={invoices.length === 0 ? 'empty' : 'filtered'}
              illustration={invoices.length === 0 ? 'facture' : undefined}
              icon={FileText}
              title={
                invoices.length === 0
                  ? 'Aucune facture émise'
                  : 'Aucune facture sélectionnée'
              }
              description={
                invoices.length === 0
                  ? 'Une facture se construit sur les volumes réellement livrés : elle porte le montant dû, les encaissements et les relances.'
                  : 'Choisissez une facture dans la liste pour voir son détail, ses encaissements et son aperçu.'
              }
              actionLabel={invoices.length === 0 ? 'Nouvelle facture' : undefined}
              onAction={openCreate}
              className="py-16"
            />
          )}
        </ColonneFixe>
      </SplitFixe>

      <Dialog open={showPreview} onOpenChange={setShowPreview}>
        <DialogContent className="max-h-[92vh] max-w-4xl overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Aperçu de la facture</DialogTitle>
          </DialogHeader>
          {currentInvoice ? <InvoiceDocumentPreview invoice={previewInvoice} /> : null}
        </DialogContent>
      </Dialog>

      <InvoiceEditorDialog
        open={showEditor}
        mode={editorMode}
        formData={formData}
        clients={clients}
        selectedInvoice={selectedInvoice}
        onChange={updateForm}
        onClose={() => setShowEditor(false)}
        onSubmit={submitEditor}
      />

      <EncaisserClient
        ouvert={Boolean(encaissement)}
        client={encaissement?.client_id}
        facture={encaissement?.id}
        onFermer={() => setEncaissement(null)}
        onTermine={apresEncaissement}
      />

      <ReminderDialog
        open={showReminderModal}
        data={reminderData}
        onChange={setReminderData}
        onClose={() => setShowReminderModal(false)}
        onSubmit={handleReminder}
      />

      <DisputeDialog
        open={showDisputeModal}
        reason={disputeReason}
        onChange={setDisputeReason}
        onClose={() => setShowDisputeModal(false)}
        onSubmit={handleDispute}
      />

      <DispatchDialog
        open={showDispatchModal}
        invoice={selectedInvoice}
        data={dispatchData}
        result={dispatchResult}
        onChange={setDispatchData}
        onClose={() => setShowDispatchModal(false)}
        onSubmit={handleDispatch}
        onCopy={copyText}
      />

      <Dialog open={showDeleteModal} onOpenChange={setShowDeleteModal}>
        <DialogContent className="max-w-sm">
          <DialogHeader>
            <DialogTitle>Supprimer la facture</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Confirmer la suppression de la facture{' '}
            <span className="font-semibold">{refFacture(selectedInvoice, 'cette facture')}</span> ?
          </p>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowDeleteModal(false)}>Annuler</Button>
            <Button className="bg-destructive hover:bg-destructive" onClick={handleDelete} disabled={!isOnline}>
              Supprimer
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/** Le badge ne s'affiche que sur les locations: « commande » est le cas par
 *  défaut, le marquer partout serait du bruit sur toute la liste. */
function InvoiceKindBadge() {
  return (
    <Badge className="border-border bg-muted/50 text-[10px] uppercase tracking-wide text-muted-foreground">
      Location
    </Badge>
  );
}

function InvoiceQueue({ invoices, selectedId, loading, compact, onSelect }) {
  if (loading) {
    return <LoadingState shape="cards" count={4} />;
  }
  if (invoices.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-border p-8 text-center text-sm text-muted-foreground">
        Aucune facture ne correspond à ces filtres.
      </div>
    );
  }
  return (
    // Deux cents factures dérouleraient la page sur plusieurs écrans: dès que
    // les deux panneaux cohabitent, c'est la colonne entière (`ColonneFixe`)
    // qui défile, sans second cadre à l'intérieur du premier.
    <ul className="space-y-2">
      {invoices.map((invoice) => {
        const selectionnee = selectedId === invoice.id;
        return (
          <li key={invoice.id}>
            <button
              type="button"
              onClick={() => onSelect(invoice)}
              aria-current={selectionnee ? 'true' : undefined}
              className={cn(
                'group flex w-full items-center gap-3 rounded-xl border bg-card text-left shadow-sm transition-colors hover:border-flame/40 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring',
                compact ? 'p-2.5' : 'p-3',
                // Liseré flamme sur la ligne ouverte: la sélection est un état
                // de la ligne, pas une couleur attribuée à une position.
                selectionnee ? 'border-flame ring-1 ring-flame/30' : 'border-border/70',
              )}
            >
              <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-xl bg-primary/10 text-primary">
                <FileText className="h-5 w-5" aria-hidden="true" />
              </span>

              <span className="min-w-0 flex-1">
                <span className="flex flex-wrap items-center gap-2">
                  <span className="truncate font-black text-foreground">
                    {refFacture(invoice)}
                  </span>
                  <InvoiceStatusBadge status={invoice.status} />
                  {invoice.invoice_kind === 'RENTAL' && <InvoiceKindBadge />}
                </span>
                {/* L'affichage compact sacrifie le nom du client: c'est la
                    seule ligne dont on peut se passer quand on balaie des
                    numéros, le montant et la date restent. */}
                {!compact && (
                  <span className="mt-0.5 block truncate text-xs text-muted-foreground">
                    {invoice.client?.name || 'Client non renseigné'}
                  </span>
                )}
                <span className="mt-1.5 flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="font-bold tabular-nums text-foreground">
                    {formatCurrency(invoice.total_amount || 0)}
                  </span>
                  <span className="text-xs text-muted-foreground">
                    {formatDate(invoice.invoice_date)}
                  </span>
                </span>
              </span>

              <ChevronRight
                className="h-4 w-4 shrink-0 text-muted-foreground/50 transition-transform duration-200 group-hover:translate-x-0.5 group-hover:text-muted-foreground"
                aria-hidden="true"
              />
            </button>
          </li>
        );
      })}
    </ul>
  );
}

function InvoiceDetail({ invoice, onEdit, onPayment, onReminder, onDispute, onDispatch, onDelete, onDownload, onPreview }) {
  // Encaissement, émission et changement de statut attendent le serveur: boutons inactifs sans lui.
  const { isOnline } = useSync();
  const canEdit = !['PAID', 'CANCELLED'].includes(invoice.status);
  const solde = Number(invoice.amount_remaining || 0);
  return (
    <section className="rounded-2xl border border-border/70 bg-card p-4 shadow-sm md:p-5">
      <div className="flex flex-col gap-4 xl:flex-row xl:items-start xl:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            {/* Le numéro est ce qu'on cite au téléphone: il reste le titre du bloc,
                en taille et en graisse modérées comme les montants. */}
            <h2 className="text-lg font-semibold leading-none tabular-nums text-foreground">
              {refFacture(invoice, 'Facture')}
            </h2>
            <InvoiceStatusBadge status={invoice.status} />
            <Badge variant="muted">{invoice.template_name || 'Standard'}</Badge>
            {invoice.invoice_kind === 'RENTAL' && <InvoiceKindBadge />}
          </div>
          <p className="mt-2 flex items-center gap-1.5 text-sm text-muted-foreground">
            <Building2 className="h-4 w-4 shrink-0" aria-hidden="true" />
            <span className="truncate">{invoice.client?.name || 'Client non renseigné'}</span>
          </p>
          {invoice.rental_contract_id && (
            // Une ligne de facture de location se conteste sur le contrat, pas
            // sur la facture: l'aller-retour doit être à un clic.
            <a
              className="mt-1 inline-block text-xs underline underline-offset-2 hover:text-flame-ink"
              href={`/rentals/${invoice.rental_contract_id}`}
            >
              Voir le contrat de location
            </a>
          )}
        </div>
        <div className="shrink-0 text-sm xl:text-right">
          <p className="text-muted-foreground">
            Émise le{' '}
            <span className="font-semibold text-foreground">{formatDate(invoice.invoice_date)}</span>
          </p>
        </div>
      </div>

      <div className="mt-4 grid gap-3 sm:grid-cols-3">
        <AmountCard label="Total facture" value={formatCurrency(invoice.total_amount || 0)} />
        <AmountCard label="Paiements affectés" value={formatCurrency(invoice.amount_paid || 0)} />
        {Number(invoice.credit_total)>0 && <AmountCard label="Avoirs appliqués" value={formatCurrency(invoice.credit_total)} />}
        {/* Un solde nul n'est pas une alerte: le rouge ne sort que s'il reste
            quelque chose à encaisser. */}
        <AmountCard label="Solde dû" value={formatCurrency(solde)} danger={solde > 0} />
      </div>

      {/* La colonne d'actions ne se met à côté du détail qu'à partir de
          1536 px. En dessous — un portable de 1440 px, le format le plus
          courant — elle volait 240 px au centre, et le détail tronquait
          justement ce qu'il existe pour montrer: « Station Balanza… »,
          « 28/07/… », la colonne Total coupée. Un écran de détail qui tronque
          son détail ne sert plus à rien. */}
      <div className="mt-4 grid gap-4 2xl:grid-cols-[minmax(0,1fr)_240px]">
        <Tabs defaultValue="details" className="min-w-0">
          <TabsList className="w-full max-w-full justify-start overflow-x-auto">
            <TabsTrigger value="details">
              <FileText className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Détails
            </TabsTrigger>
            <TabsTrigger value="historique">
              <History className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Historique
            </TabsTrigger>
            <TabsTrigger value="documents">
              <Paperclip className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Documents
            </TabsTrigger>
            <TabsTrigger value="notes">
              <StickyNote className="mr-1.5 h-4 w-4" aria-hidden="true" />
              Notes
            </TabsTrigger>
          </TabsList>

          <TabsContent value="details" className="space-y-3">
            <InvoiceLinesTable invoice={invoice} />
            <InvoiceInformations invoice={invoice} />
          </TabsContent>
          <TabsContent value="historique">
            <InvoiceHistory invoice={invoice} />
          </TabsContent>
          <TabsContent value="documents">
            <InvoiceDocuments
              invoice={invoice}
              onDownload={onDownload}
              onDispatch={onDispatch}
              onPreview={onPreview}
            />
          </TabsContent>
          <TabsContent value="notes">
            <InvoiceNotes invoice={invoice} />
          </TabsContent>
        </Tabs>

        {/* Colonne d'actions: empilée et toujours au même endroit, pour que le
            geste appris sur une facture vaille pour la suivante. */}
        {/* Empilée en colonne quand elle est à côté du détail, en rangée qui
            s'enroule quand elle passe dessous: huit boutons pleine largeur les
            uns sous les autres rallongeraient inutilement le détail. */}
        <div className="grid grid-cols-2 gap-2 sm:grid-cols-3 2xl:grid-cols-1">
          <ActionButton icon={Eye} label="Voir l’aperçu" onClick={onPreview} />
          <ActionButton icon={Download} label="Télécharger PDF" onClick={() => onDownload(invoice)} />
          <ActionButton icon={Share2} label="Partager le lien" onClick={() => onDispatch(invoice)} disabled={invoice.status === 'CANCELLED' || !isOnline} />
          <ActionButton icon={Pencil} label="Modifier" onClick={() => onEdit(invoice)} disabled={!canEdit || !isOnline} />
          <ActionButton icon={Banknote} label="Encaisser" onClick={() => onPayment(invoice)} disabled={invoice.status === 'CANCELLED' || !isOnline} />
          <ActionButton icon={MailWarning} label="Relancer" onClick={() => onReminder(invoice)} disabled={invoice.status === 'PAID' || !isOnline} />
          <ActionButton icon={Gavel} label="Marquer litige" onClick={() => onDispute(invoice)} disabled={invoice.status === 'PAID' || !isOnline} />
          <ActionButton icon={Trash2} label="Supprimer" danger onClick={() => onDelete(invoice)} disabled={(invoice.amount_paid || 0) > 0 || invoice.status === 'PAID' || !isOnline} />
        </div>
      </div>
    </section>
  );
}

function AmountCard({ label, value, danger }) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/40 p-3">
      <p className="text-[0.68rem] font-bold uppercase leading-tight tracking-[0.08em] text-muted-foreground">
        {label}
      </p>
      <p className={cn('mt-1 text-lg font-medium leading-tight tabular-nums', danger ? 'text-destructive' : 'text-foreground')}>
        {value}
      </p>
    </div>
  );
}

function InvoiceLinesTable({ invoice }) {
  const lines = lignesDeFacture(invoice);
  return (
    <div className="rounded-xl border border-border/70">
      <p className="border-b border-border/70 px-3 py-2 text-sm font-black text-foreground">
        Lignes facturées
      </p>
      {/* Le tableau défile seul: quatre colonnes chiffrées ne se compressent
          pas sous 380 px sans devenir illisibles. */}
      <div className="overflow-x-auto">
        <table className="w-full min-w-[420px] text-sm">
          <thead>
            <tr className="border-b border-border/70 text-[0.68rem] font-bold uppercase tracking-[0.08em] text-muted-foreground">
              <th scope="col" className="px-3 py-2 text-left">Produit / Service</th>
              <th scope="col" className="px-3 py-2 text-right">Qté</th>
              <th scope="col" className="px-3 py-2 text-right">Prix unitaire</th>
              <th scope="col" className="px-3 py-2 text-right">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((line, index) => (
              <tr key={`${line.description}-${index}`} className="border-b border-border/60 last:border-0">
                <td className="px-3 py-2 font-semibold text-foreground">{line.description}</td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {Number(line.quantity || 0).toLocaleString('fr-FR')}
                  {/* L'unité qualifie la quantité: « 12 » ne dit pas s'il
                      s'agit de jours de loyer ou de litres transportés. */}
                  {line.unit_label ? ` ${line.unit_label}` : ''}
                </td>
                <td className="px-3 py-2 text-right tabular-nums text-muted-foreground">
                  {formatPrixUnitaire(line.unit_price || 0)}
                </td>
                <td className="px-3 py-2 text-right font-black tabular-nums text-foreground">
                  {formatCurrency(totalLigne(line))}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function InvoiceInformations({ invoice }) {
  const infos = [
    ['Client', invoice.client?.name || 'Non renseigné'],
    ['Date d’émission', formatDate(invoice.invoice_date)],
    ['Statut', statutFacture(invoice.status)],
    // L'historique n'a pas de nature: une facture antérieure au module
    // location est une facture de commande.
    ['Nature', invoice.invoice_kind === 'RENTAL' ? 'Location' : invoice.invoice_kind === 'INTERNAL' ? 'Cession interne au prix de revient' : 'Commande'],
    ['Référence', refFacture(invoice)],
  ];
  return (
    <div className="rounded-xl border border-border/70 bg-muted/40 p-3">
      <p className="text-sm font-black text-foreground">Informations</p>
      <dl className="mt-2 grid gap-x-6 gap-y-2 sm:grid-cols-2">
        {infos.map(([label, value]) => (
          <div key={label} className="flex items-baseline justify-between gap-3 text-sm">
            <dt className="shrink-0 text-muted-foreground">{label}</dt>
            <dd className="truncate font-semibold text-foreground">{value}</dd>
          </div>
        ))}
      </dl>
    </div>
  );
}

function InvoiceHistory({ invoice }) {
  const paiements = invoice.payments || [];
  const relances = invoice.reminders || [];
  if (!paiements.length && !relances.length) {
    return (
      <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        Aucun encaissement ni relance enregistré sur cette facture.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {paiements.length > 0 && (
        <div className="rounded-xl border border-border/70 p-3">
          <p className="text-sm font-black text-foreground">Encaissements</p>
          <ul className="mt-2 space-y-2">
            {paiements.map((paiement, index) => (
              <li
                key={paiement.id || `${paiement.payment_date}-${index}`}
                className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1 rounded-lg bg-muted/40 px-3 py-2 text-sm"
              >
                <span className="font-semibold text-foreground">
                  {formatCurrency(paiement.amount || 0)}
                  <span className="ml-2 font-normal text-muted-foreground">
                    {libelle('paymentMethod', paiement.payment_method, 'Moyen non précisé')}
                  </span>
                </span>
                <span className="text-xs text-muted-foreground">
                  {formatDate(paiement.payment_date)}
                  {paiement.reference ? ` · ${paiement.reference}` : ''}
                </span>
              </li>
            ))}
          </ul>
        </div>
      )}
      {relances.length > 0 && (
        <div className="rounded-xl border border-border/70 p-3">
          <p className="text-sm font-black text-foreground">Relances</p>
          <ul className="mt-2 space-y-2">
            {relances.map((relance, index) => (
              <li
                key={relance.id || `${relance.due_date}-${index}`}
                className="rounded-lg bg-muted/40 px-3 py-2 text-sm"
              >
                <div className="flex flex-wrap items-baseline justify-between gap-x-3 gap-y-1">
                  <span className="font-semibold text-foreground">Suivi au {formatDate(relance.due_date)}</span>
                  <Badge variant="muted">{relance.status === 'DONE' ? 'Traitée' : 'En attente'}</Badge>
                </div>
                {relance.message && (
                  <p className="mt-1 text-xs text-muted-foreground">{relance.message}</p>
                )}
              </li>
            ))}
          </ul>
        </div>
      )}
    </div>
  );
}

function InvoiceDocuments({ invoice, onDownload, onDispatch, onPreview }) {
  const { isOnline } = useSync();
  return (
    <div className="rounded-xl border border-border/70 bg-muted/40 p-3">
      <div className="flex items-center gap-1.5">
        <p className="text-sm font-black text-foreground">Pièces disponibles</p>
        {/* Rien n'est stocké ici: le PDF est rendu à la demande et le lien de
            partage est émis à l'envoi. L'explication reste derrière l'icône. */}
        <Aide titre="Pièces disponibles">
          Le PDF est généré à la demande depuis le modèle «&nbsp;{normalizeTemplate(invoice.template_name)}&nbsp;».
          Le lien client est créé au moment du partage, avec la durée de validité choisie.
        </Aide>
      </div>
      <div className="mt-3 flex flex-wrap gap-2">
        <Button variant="outline" size="sm" onClick={onPreview}>
          <Eye className="h-4 w-4" aria-hidden="true" />
          Voir l’aperçu
        </Button>
        <Button variant="outline" size="sm" onClick={() => onDownload(invoice)}>
          <Download className="h-4 w-4" aria-hidden="true" />
          Télécharger PDF
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => onDispatch(invoice)}
          disabled={invoice.status === 'CANCELLED' || !isOnline}
        >
          <Share2 className="h-4 w-4" aria-hidden="true" />
          Partager le lien
        </Button>
      </div>
    </div>
  );
}

function InvoiceNotes({ invoice }) {
  const { notes, terms_and_conditions: conditions, dispute_reason: litige } = invoice;
  if (!notes && !conditions && !litige) {
    return (
      <p className="rounded-xl border border-dashed border-border p-6 text-center text-sm text-muted-foreground">
        Aucune note ni condition particulière sur cette facture.
      </p>
    );
  }
  return (
    <div className="space-y-3">
      {/* Le litige passe avant: c'est lui qui explique pourquoi la facture ne
          rentre pas. */}
      {litige && (
        <div className="rounded-xl border border-destructive/30 bg-destructive/10 p-3">
          <p className="text-sm font-black text-destructive">Motif du litige</p>
          <p className="mt-1 text-sm text-muted-foreground">{francisaTexte(litige)}</p>
        </div>
      )}
      {/* Texte libre: une date ISO saisie ou importée se lit à la française. */}
      {notes && <NoteBlock title="Notes">{francisaTexte(notes)}</NoteBlock>}
      {conditions && <NoteBlock title="Conditions">{francisaTexte(conditions)}</NoteBlock>}
    </div>
  );
}

function NoteBlock({ title, children }) {
  return (
    <div className="rounded-xl border border-border/70 bg-muted/40 p-3">
      <p className="text-sm font-black text-foreground">{title}</p>
      <p className="mt-1 whitespace-pre-line text-sm text-muted-foreground">{children}</p>
    </div>
  );
}

function InvoiceBusinessReadiness({ invoice }) {
  const form = invoiceToForm(invoice);
  return (
    <BusinessActionContract
      title={`${LEXIQUE.conditionsAction} cette facture`}
      contract={invoiceContract({ invoice, formData: form, mode: 'edit' })}
      blockers={invoiceBlockers({ invoice, formData: form, mode: 'edit' })}
      compact
    />
  );
}

function MiniFact({ label, value, strong, danger }) {
  return (
    <div className="rounded-lg border border-border bg-muted/50 p-3">
      <p className="text-xs font-black uppercase tracking-wide text-muted-foreground">{label}</p>
      <p className={cn('mt-1 text-sm', strong && 'font-black text-foreground', danger && 'font-black text-flame-ink')}>{value}</p>
    </div>
  );
}

function ActionButton({ icon: Icon, label, danger, ...props }) {
  return (
    <Button
      variant={danger ? 'ghost' : 'outline'}
      className={cn('w-full justify-start', danger && 'text-destructive hover:text-destructive')}
      {...props}
    >
      <Icon className="h-4 w-4" />
      {label}
    </Button>
  );
}

function InvoiceEditorDialog({ open, mode, formData, clients, selectedInvoice, onChange, onClose, onSubmit }) {
  const { isOnline } = useSync();
  const blockers = invoiceBlockers({ invoice: selectedInvoice, formData, mode });
  const clientOptions = useMemo(() => clients.map((client) => ({
    value: client.id,
    label: client.name || 'Client sans nom',
    description: client.city || undefined,
    keywords: [client.contact_name, client.phone, client.tax_id],
  })), [clients]);
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-h-[92vh] max-w-6xl overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{mode === 'edit' ? 'Modifier la facture' : 'Nouvelle facture'}</DialogTitle>
        </DialogHeader>
        <div className="grid gap-5 lg:grid-cols-[1fr_390px]">
          <div className="space-y-4">
            {/* Une facture, c'est un client et ce qu'on lui facture. Le
                numéro est écrasé par une séquence atomique côté serveur et la
                date d'émission est celle du jour: rien de tout cela n'a à être
                saisi pour émettre. */}
            <Field label="Client">
              <SearchableSelect
                value={formData.client_id || ''}
                onValueChange={(client_id) => onChange({ client_id })}
                options={clientOptions}
                placeholder="Sélectionner un client"
                searchPlaceholder="Rechercher un client…"
                emptyMessage="Aucun client trouvé"
              />
            </Field>
            <InvoiceLineItemsEditor lines={formData.line_items} onChange={(line_items) => onChange({ line_items })} />

            <ChampsAvances
              titre="Plus de détails"
              consequence="Modèle du document, dates, TVA, notes et conditions."
              nombre={5}
              valeurs={[
                // En modification, les dates de la facture sont des données
                // enregistrées et le repli doit les montrer. À la création
                // elles sont posées par le formulaire — les compter ouvrirait
                // le bloc à chaque nouvelle facture.
                formData.template_name === 'Standard' ? '' : formData.template_name,
                mode === 'edit' ? formData.invoice_date : '',
                Number(formData.tax_rate || 0) > 0 ? formData.tax_rate : '',
                formData.notes,
                formData.terms_and_conditions === CONDITIONS_PAR_DEFAUT
                  ? ''
                  : formData.terms_and_conditions,
              ]}
            >
              <Field label="Modèle">
                <InvoiceTemplatePicker value={formData.template_name} onChange={(template_name) => onChange({ template_name })} />
              </Field>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Date facture">
                  <Input type="date" value={formData.invoice_date} onChange={(event) => onChange({ invoice_date: event.target.value })} />
                </Field>
                <Field label="Taux de TVA (0,18 pour 18 %)">
                  <Input type="number" step="0.01" min="0" max="1" placeholder="0,18" value={formData.tax_rate} onChange={(event) => onChange({ tax_rate: event.target.value })} />
                </Field>
              </div>
              <div className="grid gap-3 md:grid-cols-2">
                <Field label="Notes">
                  <Textarea value={formData.notes} onChange={(event) => onChange({ notes: event.target.value })} />
                </Field>
                <Field label="Conditions">
                  <Textarea value={formData.terms_and_conditions} onChange={(event) => onChange({ terms_and_conditions: event.target.value })} />
                </Field>
              </div>
            </ChampsAvances>

            <BusinessActionContract
              title={`${LEXIQUE.conditionsAction} cette facture`}
              contract={invoiceContract({ invoice: selectedInvoice, formData, mode })}
              blockers={blockers}
              compact
            />
          </div>
          <div className="space-y-3">
            <TotalsPanel formData={formData} />
            <InvoiceDocumentPreview invoice={buildPreviewInvoice(formData, new Map(clients.map((client) => [client.id, client])))} compact />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onSubmit} disabled={!isOnline}>{mode === 'edit' ? 'Enregistrer' : 'Créer'}</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function InvoiceTemplatePicker({ value, onChange }) {
  return (
    <div className="grid grid-cols-3 gap-2">
      {Object.entries(templateOptions).map(([key, template]) => (
        <button
          key={key}
          type="button"
          onClick={() => onChange(key)}
          className={cn(
            'rounded-lg border p-2 text-left transition hover:border-flame/50',
            template.accent,
            value === key && 'ring-2 ring-flame/50',
          )}
        >
          <span className={cn('mb-2 block h-2 rounded-full', template.band)} />
          <span className="block text-xs font-black text-foreground">{template.label}</span>
          <span className="mt-1 block text-[10px] leading-4 text-muted-foreground">{template.description}</span>
        </button>
      ))}
    </div>
  );
}

function InvoiceLineItemsEditor({ lines, onChange }) {
  const updateLine = (index, patch) => {
    onChange(lines.map((line, lineIndex) => (lineIndex === index ? { ...line, ...patch } : line)));
  };
  const removeLine = (index) => {
    onChange(lines.filter((_, lineIndex) => lineIndex !== index));
  };
  return (
    <div className="rounded-lg border border-border bg-muted/50 p-3">
      <div className="flex items-center justify-between">
        <p className="font-black text-foreground">Lignes de facture</p>
        <Button variant="outline" size="sm" onClick={() => onChange([...lines, defaultLine()])}>
          <Plus className="h-4 w-4" />
          Ligne
        </Button>
      </div>
      <div className="mt-3 space-y-2">
        {lines.map((line, index) => (
          <div key={line.id} className="grid gap-2 rounded-md bg-card p-3 md:grid-cols-[1fr_90px_130px_36px]">
            <Input placeholder="Description" value={line.description} onChange={(event) => updateLine(index, { description: event.target.value })} />
            <Input type="number" placeholder="Qté" value={line.quantity} onChange={(event) => updateLine(index, { quantity: event.target.value })} />
            <Input type="number" placeholder="Prix unitaire" value={line.unit_price} onChange={(event) => updateLine(index, { unit_price: event.target.value })} />
            <Button variant="ghost" size="icon" className="text-destructive" onClick={() => removeLine(index)} disabled={lines.length === 1}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
      </div>
    </div>
  );
}

function TotalsPanel({ formData }) {
  return (
    <div className="rounded-lg border border-border bg-card p-4 shadow-sm">
      <p className="text-sm font-black text-foreground">Totaux calculés</p>
      <div className="mt-3 space-y-2 text-sm">
        <Line label="Sous-total" value={formatCurrency(formData.subtotal)} />
        <Line label="TVA" value={formatCurrency(formData.tax_amount)} />
        <Line label="Net à payer" value={formatCurrency(formData.total_amount)} strong />
      </div>
    </div>
  );
}

function InvoiceDocumentPreview({ invoice, compact = false }) {
  if (!invoice) {
    return (
      <div className="rounded-lg border border-dashed border-border bg-card p-8 text-center text-muted-foreground">
        <FileText className="mx-auto mb-3 h-8 w-8 text-muted-foreground/70" />
        Aperçu facture indisponible.
      </div>
    );
  }
  const template = normalizeTemplate(invoice.template_name);
  const theme = templateOptions[template];
  const lines = lignesDeFacture(invoice);
  const paid = Number(invoice.amount_paid ?? invoice.paid_amount ?? 0);
  const remaining = Number(invoice.amount_remaining ?? invoice.balance_due ?? Math.max(Number(invoice.total_amount || 0) - paid, 0));
  return (
    // La feuille est posée sur `bg-muted` et non sur un blanc en dur: en thème
    // sombre, l'aperçu reste un document clair par rapport à son support, sans
    // devenir une dalle blanche au milieu d'un écran noir.
    <div className="rounded-2xl border border-border/70 bg-muted p-3 shadow-sm">
      <div className={cn('mx-auto w-full bg-card shadow-lg', compact ? 'p-5' : 'max-w-[620px] p-6 md:p-7')}>
        <div className={cn('rounded-xl p-4', theme.band, theme.bandText)}>
          <div className="flex flex-wrap items-start justify-between gap-3">
            <div>
              <p className="text-xl font-black tracking-wide md:text-2xl">E-CARBU TRANSPORT</p>
              <p className="text-xs opacity-80">Transport et distribution de carburant</p>
            </div>
            <div className="text-right">
              <p className="text-lg font-black md:text-xl">
                {template === 'Proforma' ? 'PROFORMA' : 'FACTURE'} N°
              </p>
              <p className="text-xs opacity-80">{invoice.invoice_number || 'N° à générer'}</p>
            </div>
          </div>
        </div>
        {template === 'Proforma' && (
          <div className="mt-3 rounded-md border border-info/30 bg-info/10 p-2 text-center text-xs font-bold text-info">
            Document non définitif
          </div>
        )}

        {/* Émetteur et client côte à côte: c'est la lecture qu'un client fait
            en premier — qui réclame, et à qui. */}
        <div className="mt-5 grid gap-3 text-xs sm:grid-cols-2">
          <PreviewBox title="Émetteur">
            <p className="font-black text-foreground">E-Carbu Transport</p>
            <p>Transport de carburant</p>
          </PreviewBox>
          <PreviewBox title="Facturé à">
            <p className="font-black text-foreground">{invoice.client?.name || 'Client'}</p>
            <p>{invoice.client?.phone || invoice.client?.email || 'Coordonnées non renseignées'}</p>
          </PreviewBox>
        </div>

        <div className="mt-3 grid gap-3 text-xs sm:grid-cols-2">
          <PreviewBox title="Informations">
            <Line label="Date" value={formatDate(invoice.invoice_date)} />
            <Line label="Statut" value={statutFacture(invoice.status)} />
          </PreviewBox>
          {/* L'encadré du montant est ce qu'on cherche des yeux sur un PDF
              reçu: il est isolé. */}
          <div className="rounded-md border border-border bg-muted/50 p-3">
            <p className="text-[10px] font-black uppercase tracking-wide text-muted-foreground">
              Montant total
            </p>
            <p className="kpi-value mt-1 text-foreground">
              {formatCurrency(invoice.total_amount || 0)}
            </p>
          </div>
        </div>

        <div className="mt-5 overflow-hidden rounded-lg border border-border">
          <div className="grid grid-cols-[1fr_70px_90px] bg-readout px-3 py-2 text-xs font-black uppercase text-readout-foreground">
            <span>Description</span>
            <span className="text-right">Qté</span>
            <span className="text-right">Total</span>
          </div>
          {lines.map((line, index) => (
            <div key={`${line.description}-${index}`} className="grid grid-cols-[1fr_70px_90px] border-t border-border/60 px-3 py-2 text-xs">
              <span className="font-semibold text-foreground">{line.description}</span>
              <span className="text-right tabular-nums text-muted-foreground">{Number(line.quantity || 0).toLocaleString('fr-FR')}</span>
              <span className="text-right font-medium tabular-nums">{formatCurrency(totalLigne(line))}</span>
            </div>
          ))}
        </div>
        <div className="ml-auto mt-5 w-full max-w-[260px] space-y-2 text-sm">
          <Line label="Sous-total" value={formatCurrency(invoice.subtotal || 0)} />
          <Line label="TVA" value={formatCurrency(invoice.tax_amount || 0)} />
          <Line label="Net à payer" value={formatCurrency(invoice.total_amount || 0)} strong />
          {Number(invoice.credit_total)>0 && <Line label="Avoirs" value={formatCurrency(invoice.credit_total)} />}
          {paid > 0 && <Line label="Déjà payé" value={formatCurrency(paid)} />}
          <Line label="Solde" value={formatCurrency(remaining)} strong />
        </div>
        {(invoice.notes || invoice.terms_and_conditions) && (
          <div className="mt-5 grid gap-3 text-xs text-muted-foreground">
            {invoice.notes && <PreviewBox title="Notes">{invoice.notes}</PreviewBox>}
            {invoice.terms_and_conditions && <PreviewBox title="Conditions">{invoice.terms_and_conditions}</PreviewBox>}
          </div>
        )}
      </div>
    </div>
  );
}

function ReminderDialog({ open, data, onChange, onClose, onSubmit }) {
  const { isOnline } = useSync();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Relancer le paiement</DialogTitle></DialogHeader>
        <div className="space-y-3">
          <Field label="Message">
            <Textarea value={data.message} onChange={(event) => onChange((prev) => ({ ...prev, message: event.target.value }))} />
          </Field>
          <Field label="Date de suivi">
            <Input type="date" value={data.due_date} onChange={(event) => onChange((prev) => ({ ...prev, due_date: event.target.value }))} />
          </Field>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onSubmit} disabled={!isOnline}>Ajouter relance</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DisputeDialog({ open, reason, onChange, onClose, onSubmit }) {
  const { isOnline } = useSync();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-md">
        <DialogHeader><DialogTitle>Marquer en litige</DialogTitle></DialogHeader>
        <Field label="Motif">
          <Textarea value={reason} onChange={(event) => onChange(event.target.value)} />
        </Field>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Annuler</Button>
          <Button onClick={onSubmit} disabled={!isOnline}>Enregistrer litige</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function DispatchDialog({ open, invoice, data, result, onChange, onClose, onSubmit, onCopy }) {
  const { isOnline } = useSync();
  return (
    <Dialog open={open} onOpenChange={onClose}>
      <DialogContent className="max-w-2xl">
        <DialogHeader><DialogTitle>Envoyer / partager la facture</DialogTitle></DialogHeader>
        <div className="space-y-4">
          <div className="grid gap-3 md:grid-cols-2">
            <MiniFact label="Facture" value={refFacture(invoice)} strong />
            <MiniFact label="Client" value={invoice?.client?.name || 'Non renseigné'} />
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Canal">
              <Select value={data.channel} onValueChange={(channel) => onChange((prev) => ({ ...prev, channel }))}>
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="EMAIL">Courriel</SelectItem>
                  <SelectItem value="WHATSAPP">WhatsApp</SelectItem>
                  <SelectItem value="LINK">Lien sécurisé</SelectItem>
                </SelectContent>
              </Select>
            </Field>
            <Field label={data.channel === 'EMAIL' ? 'Courriel du destinataire' : 'Destinataire'}>
              <Input
                type={data.channel === 'EMAIL' ? 'email' : 'text'}
                value={data.recipient}
                onChange={(event) => onChange((prev) => ({ ...prev, recipient: event.target.value }))}
                placeholder={data.channel === 'EMAIL' ? 'client@exemple.com' : 'Nom ou numéro'}
              />
            </Field>
          </div>
          <div className="grid gap-3 md:grid-cols-2">
            <Field label="Le lien reste valable">
              <Select
                value={data.expires_in_seconds}
                onValueChange={(expires_in_seconds) => onChange((prev) => ({ ...prev, expires_in_seconds }))}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  {VALIDITES_DE_LIEN.map(({ valeur, libelle }) => (
                    <SelectItem key={valeur} value={valeur}>{libelle}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </Field>
            <Field label="Message personnalisé">
              <Textarea
                value={data.message}
                onChange={(event) => onChange((prev) => ({ ...prev, message: event.target.value }))}
                placeholder="Laissez vide pour utiliser le message E-Carbu."
              />
            </Field>
          </div>

          {result?.share_url && (
            <div className="rounded-lg border border-border bg-muted/50 p-4">
              <p className="text-sm font-black text-foreground">Lien prêt à envoyer</p>
              <div className="mt-3 space-y-2 text-sm">
                <div className="rounded-md border border-border bg-card p-3">
                  <p className="text-xs font-black uppercase tracking-wide text-muted-foreground">Lien sécurisé</p>
                  <p className="mt-1 break-all text-muted-foreground">{result.share_url}</p>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button type="button" variant="outline" onClick={() => onCopy(result.share_url, 'Lien sécurisé copié')}>
                    <Copy className="h-4 w-4" />
                    Copier le lien
                  </Button>
                  {result.mailto_url && (
                    <Button type="button" variant="outline" onClick={() => window.open(result.mailto_url, '_blank')}>
                      <Send className="h-4 w-4" />
                      Ouvrir le courriel
                    </Button>
                  )}
                  {result.whatsapp_url && (
                    <Button type="button" variant="outline" onClick={() => window.open(result.whatsapp_url, '_blank')}>
                      <Share2 className="h-4 w-4" />
                      Ouvrir WhatsApp
                    </Button>
                  )}
                </div>
              </div>
            </div>
          )}
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>Fermer</Button>
          <Button onClick={onSubmit} disabled={!isOnline}>Préparer l’envoi</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function Field({ label, children }) {
  return (
    <div className="space-y-1">
      <Label>{label}</Label>
      {children}
    </div>
  );
}

function PreviewBox({ title, children }) {
  return (
    <div className="rounded-md border border-border bg-muted/50 p-3">
      <p className="mb-1 text-[10px] font-black uppercase tracking-wide text-muted-foreground">{title}</p>
      <div>{children}</div>
    </div>
  );
}

function Line({ label, value, strong }) {
  return (
    <div className={cn('flex items-center justify-between gap-3', strong && 'font-black text-foreground')}>
      <span>{label}</span>
      <span>{value}</span>
    </div>
  );
}

function InvoiceStatusBadge({ status }) {
  const cls = {
    PAID: 'border-success/30 bg-success/10 text-success',
    DISPUTED: 'border-flame/30 bg-flame/10 text-flame-ink',
    PARTIAL: 'border-info/30 bg-info/10 text-info',
    PARTIALLY_PAID: 'border-info/30 bg-info/10 text-info',
    CANCELLED: 'border-border bg-muted text-muted-foreground',
  }[status] || 'border-border bg-muted/50 text-muted-foreground';
  return <Badge className={cls}>{statutFacture(status)}</Badge>;
}

export function buildInvoicePayload(formData) {
  return {
    client_id: formData.client_id,
    order_ids: [],
    invoice_date: formData.invoice_date,
    subtotal: formData.subtotal,
    tax_rate: Number(formData.tax_rate || 0),
    tax_amount: formData.tax_amount,
    total_amount: formData.total_amount,
    template_name: formData.template_name,
    notes: formData.notes || undefined,
    terms_and_conditions: formData.terms_and_conditions || undefined,
    line_items: formData.line_items.map((line) => ({
      description: line.description,
      quantity: Number(line.quantity || 0),
      unit_price: Number(line.unit_price || 0),
      total: arrondiDevise(Number(line.quantity || 0) * Number(line.unit_price || 0)),
      order_id: line.order_id || undefined,
      trip_id: line.trip_id || undefined,
    })),
  };
}

function buildPreviewInvoice(formData, clientsById) {
  const payload = buildInvoicePayload(formData);
  return {
    ...payload,
    id: 'preview',
    invoice_number: 'Aperçu',
    status: 'DRAFT',
    amount_paid: 0,
    paid_amount: 0,
    amount_remaining: payload.total_amount,
    balance_due: payload.total_amount,
    client: clientsById.get(formData.client_id),
  };
}
